/** Routine equipment readings outside a maintenance intervention. */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type ReactElement, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "@/api/client";
import { unwrap } from "@/api/problem";
import { useAuth } from "@/app/auth";
import type { Asset } from "@/features/assets/queries";
import { invalidateMeterReading } from "@/features/assets/meter-cache";
import { FormDialog } from "@/shared/components/form-dialog";
import { useForm } from "@/shared/lib/form";
import { Field, Input, Select, Textarea } from "@/shared/ui/form";

/** Validated routine reading dialog with explicit reset/source evidence. */
export function AssetMeterReading({
  asset,
  onClose,
}: {
  asset: Pick<Asset, "id" | "meters">;
  onClose: () => void;
}): ReactElement {
  const { t } = useTranslation();
  const { can } = useAuth();
  const client = useQueryClient();
  const [sourceRef] = useState(() => crypto.randomUUID());
  const form = useForm({
    initial: {
      meter_id: asset.meters?.[0]?.id ?? "",
      value: "",
      note: "",
      read_at: "",
      is_reset: false,
    },
    idPrefix: "asset-reading",
  });
  const mutation = useMutation({
    mutationFn: async (values: typeof form.values) =>
      unwrap(
        await api.POST("/api/v1/meters/{meter_id}/readings", {
          params: { path: { meter_id: values.meter_id } },
          body: {
            value: values.value,
            note: values.note,
            read_at: values.read_at ? new Date(values.read_at).toISOString() : null,
            is_reset: values.is_reset,
            source: "manual",
            source_ref: sourceRef,
          },
        }),
      ),
    onSuccess: () => invalidateMeterReading(client),
  });
  return (
    <FormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title={t("assets.recordReading")}
      submitting={form.submitting}
      error={form.errors._}
      onSubmit={async () => {
        if (await form.submit((values) => mutation.mutateAsync(values))) onClose();
      }}
    >
      <Field id="asset-reading-meter_id" label={t("assets.meters")}>
        <Select {...form.field("meter_id")} required>
          {(asset.meters ?? []).map((meter) => (
            <option key={meter.id} value={meter.id}>
              {meter.name} ({meter.unit})
            </option>
          ))}
        </Select>
      </Field>
      <Field id="asset-reading-value" label={t("assets.readingValue")} error={form.errors.value}>
        <Input {...form.field("value")} type="number" min="0" step="any" required />
      </Field>
      <Field id="asset-reading-read_at" label={t("assets.readAt")} error={form.errors.read_at}>
        <Input {...form.field("read_at")} type="datetime-local" />
      </Field>
      <Field id="asset-reading-note" label={t("app.notes")} error={form.errors.note}>
        <Textarea {...form.field("note")} />
      </Field>
      {can("meters.meter.manage") ? (
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.values.is_reset}
            onChange={(event) => form.set("is_reset", event.target.checked)}
          />
          {t("assets.counterReset")}
        </label>
      ) : null}
    </FormDialog>
  );
}
